import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/auth/auth.service';
import { Roles } from 'src/app/shared/models/auth/role.model';
import { Employee } from 'src/app/shared/models/employee.model';
import { SystemSettings } from 'src/app/shared/models/system-settings.model';
import { EmployeeService } from 'src/app/shared/services/employee.service';
import { SystemSettingsService } from 'src/app/shared/services/system-settings.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html'
})
export class SettingsComponent implements OnInit {
  settings: SystemSettings;
  allActiveEmployees: Employee[] = [];
  availableEmployees: Employee[] = [];
  selectedEmployeeId: number | null = null;
  description = '';
  isLoading = true;
  isSavingDescription = false;
  isAddingApprover = false;
  removingApproverId: number | null = null;

  constructor(
    private systemSettingsService: SystemSettingsService,
    private employeeService: EmployeeService,
    private authService: AuthService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    if (!this.authService.hasRole(Roles.Admin)) {
      this.router.navigate(['/']);
      return;
    }
    this.loadSettings();
    this.employeeService.activeEmployees$.subscribe(employees => {
      this.allActiveEmployees = employees;
      this.refreshAvailableEmployees();
    });
  }

  private loadSettings() {
    this.isLoading = true;
    this.systemSettingsService.getSettings().subscribe(settings => {
      this.settings = settings;
      this.settings.systemFormalApprovers = this.sortByFirstName(settings.systemFormalApprovers || []);
      this.description = settings.formalApproverSectionDescription || '';
      this.refreshAvailableEmployees();
      this.isLoading = false;
    });
  }

  private refreshAvailableEmployees() {
    if (!this.settings) return;
    const approverIds = new Set((this.settings.systemFormalApprovers || []).map(a => a.id));
    this.availableEmployees = this.sortByFirstName(this.allActiveEmployees.filter(e => !approverIds.has(e.id)));
  }

  // Lista jest prezentowana jako "Imię Nazwisko", więc sortujemy tak, jak wyświetlamy.
  // Pracuje na kopii — allActiveEmployees to współdzielona (shareReplay) tablica,
  // której kolejności "po nazwisku" nie wolno przestawiać innym ekranom.
  private sortByFirstName(employees: Employee[]): Employee[] {
    return employees.slice().sort((a, b) => {
      const firstName = this.comparePl(a.person && a.person.firstName, b.person && b.person.firstName);
      return firstName !== 0 ? firstName : this.comparePl(a.person && a.person.lastName, b.person && b.person.lastName);
    });
  }

  private comparePl(a: string, b: string): number {
    return (a || '').localeCompare(b || '', 'pl', { sensitivity: 'base' });
  }

  saveDescription() {
    this.isSavingDescription = true;
    this.systemSettingsService.updateDescription(this.description).subscribe({
      next: () => {
        this.toastr.success('Opis zapisany.');
        this.isSavingDescription = false;
      },
      error: () => {
        this.toastr.error('Błąd podczas zapisywania opisu.');
        this.isSavingDescription = false;
      }
    });
  }

  addApprover() {
    if (!this.selectedEmployeeId) return;
    this.isAddingApprover = true;
    this.systemSettingsService.addFormalApprover(this.selectedEmployeeId).subscribe({
      next: () => {
        this.toastr.success('Zastępca dodany.');
        this.selectedEmployeeId = null;
        this.isAddingApprover = false;
        this.loadSettings();
      },
      error: () => {
        this.toastr.error('Błąd podczas dodawania zastępcy.');
        this.isAddingApprover = false;
      }
    });
  }

  removeApprover(employeeId: number) {
    this.removingApproverId = employeeId;
    this.systemSettingsService.removeFormalApprover(employeeId).subscribe({
      next: () => {
        this.toastr.success('Zastępca usunięty.');
        this.removingApproverId = null;
        this.loadSettings();
      },
      error: () => {
        this.toastr.error('Błąd podczas usuwania zastępcy.');
        this.removingApproverId = null;
      }
    });
  }
}
